/**
 * Ticket Table Component
 * Displays tickets in a table/list format with columns and sorting
 */

import { useState, useMemo } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  TableSortLabel,
} from '@mui/material'
import { ConfirmationNumber } from '@mui/icons-material'
import { TicketSummaryDto, TicketPriority, TicketStatus } from '@api/types/tickets'
import { TicketStatusBadge } from './TicketStatusBadge'
import { TicketPriorityIndicator } from './TicketPriorityIndicator'
import { TicketTypeIcon } from './TicketTypeIcon'
import { safeFormatDistanceToNow } from '@utils/dateUtils'
import { useAuthStore } from '@store/authStore'

interface TicketTableProps {
  tickets: TicketSummaryDto[]
  loading?: boolean
  error?: string | null
  emptyMessage?: string
  onTicketClick?: (ticketId: string) => void
  showAssignee?: boolean
}

type SortField = 'title' | 'status' | 'priority' | 'claimedBy' | 'comments' | 'lastUpdated'
type SortOrder = 'asc' | 'desc'

export function TicketTable({
  tickets,
  loading = false,
  error = null,
  emptyMessage = 'No tickets found',
  onTicketClick,
  showAssignee = false,
}: TicketTableProps) {
  const { user } = useAuthStore()
  const [sortField, setSortField] = useState<SortField>('lastUpdated')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      // Default to ascending for new field
      setSortField(field)
      setSortOrder('asc')
    }
  }

  // Sort tickets based on current sort field and order
  const sortedTickets = useMemo(() => {
    const sorted = [...tickets].sort((a, b) => {
      let aValue: any
      let bValue: any

      switch (sortField) {
        case 'title':
          aValue = a.title.toLowerCase()
          bValue = b.title.toLowerCase()
          break
        case 'status':
          aValue = a.status
          bValue = b.status
          break
        case 'priority':
          aValue = a.priority
          bValue = b.priority
          break
        case 'claimedBy':
          aValue = a.claimedByDisplayName?.toLowerCase() || ''
          bValue = b.claimedByDisplayName?.toLowerCase() || ''
          break
        case 'comments':
          aValue = a.commentCount
          bValue = b.commentCount
          break
        case 'lastUpdated':
          aValue = new Date(a.lastUpdatedAt).getTime()
          bValue = new Date(b.lastUpdatedAt).getTime()
          break
        default:
          return 0
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })

    return sorted
  }, [tickets, sortField, sortOrder])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    )
  }

  if (tickets.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          py: 4,
        }}
      >
        <ConfirmationNumber sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    )
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width="40px">Type</TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === 'title'}
                direction={sortField === 'title' ? sortOrder : 'asc'}
                onClick={() => handleSort('title')}
              >
                Title
              </TableSortLabel>
            </TableCell>
            <TableCell width="120px">
              <TableSortLabel
                active={sortField === 'status'}
                direction={sortField === 'status' ? sortOrder : 'asc'}
                onClick={() => handleSort('status')}
              >
                Status
              </TableSortLabel>
            </TableCell>
            <TableCell width="120px">
              <TableSortLabel
                active={sortField === 'priority'}
                direction={sortField === 'priority' ? sortOrder : 'asc'}
                onClick={() => handleSort('priority')}
              >
                Priority
              </TableSortLabel>
            </TableCell>
            {showAssignee && (
              <TableCell width="150px">
                <TableSortLabel
                  active={sortField === 'claimedBy'}
                  direction={sortField === 'claimedBy' ? sortOrder : 'asc'}
                  onClick={() => handleSort('claimedBy')}
                >
                  Claimed By
                </TableSortLabel>
              </TableCell>
            )}
            <TableCell width="100px">
              <TableSortLabel
                active={sortField === 'comments'}
                direction={sortField === 'comments' ? sortOrder : 'asc'}
                onClick={() => handleSort('comments')}
              >
                Comments
              </TableSortLabel>
            </TableCell>
            <TableCell width="150px">
              <TableSortLabel
                active={sortField === 'lastUpdated'}
                direction={sortField === 'lastUpdated' ? sortOrder : 'asc'}
                onClick={() => handleSort('lastUpdated')}
              >
                Last Updated
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedTickets.map((ticket) => {
            // Normalize IDs for comparison (handle case, whitespace, hyphens in GUIDs)
            const normalizeId = (id: string | null | undefined): string => {
              if (!id) return ''
              return id.toLowerCase().trim().replace(/[-\s]/g, '')
            }

            const isClaimedByOther = ticket.claimedByUserId && normalizeId(ticket.claimedByUserId) !== normalizeId(user?.userId)

            return (
              <TableRow
                key={ticket.id}
                hover
                sx={{
                  cursor: 'pointer',
                  ...(isClaimedByOther && {
                    backgroundColor: 'warning.light',
                    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9))',
                  }),
                }}
                onClick={() => onTicketClick?.(ticket.id)}
              >
                {/* Type Icon */}
                <TableCell>
                  <TicketTypeIcon type={ticket.type} size="small" />
                </TableCell>

                {/* Title with badges */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {ticket.title}
                    </Typography>
                    {isClaimedByOther && (
                      <Chip label="CLAIMED" size="small" color="warning" sx={{ fontSize: '0.7rem', height: 20 }} />
                    )}
                    {ticket.isEscalated && (
                      <Chip label="ESCALATED" size="small" color="error" sx={{ fontSize: '0.7rem', height: 20 }} />
                    )}
                  </Box>
                </TableCell>

                {/* Status */}
                <TableCell>
                  <TicketStatusBadge status={ticket.status} />
                </TableCell>

                {/* Priority */}
                <TableCell>
                  <TicketPriorityIndicator priority={ticket.priority} />
                </TableCell>

                {/* Claimed By (if showing assignees) */}
                {showAssignee && (
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {ticket.claimedByDisplayName || '-'}
                    </Typography>
                  </TableCell>
                )}

                {/* Comment Count */}
                <TableCell align="center">
                  <Typography variant="body2">{ticket.commentCount}</Typography>
                </TableCell>

                {/* Last Updated */}
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {safeFormatDistanceToNow(ticket.lastUpdatedAt)}
                  </Typography>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
